import React from 'react';
import { Link } from 'react-router-dom';
import { PhotoIcon } from '@heroicons/react/24/solid';
import { SellerOrderItem } from '@shopify-multiseller/graphql/partner/types';
import { Badge } from '../../components/primitives';
import { SectionCard } from '../../components/SectionCard';
import { formatCents } from '../../helpers/formatters';

/** One deduction taken against an item, on the quiet ground under it. */
const FeeLine: React.FC<{ label: string; amount: number; currencyCode: string }> = ({
  label,
  amount,
  currencyCode,
}) => (
  <div className="flex items-baseline justify-between gap-4 py-0.5">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="text-xs font-medium tabular-nums text-foreground">
      {`−${formatCents({ amount, currencyCode })}`}
    </span>
  </div>
);

const ItemRow: React.FC<{ item: SellerOrderItem; currencyCode: string }> = ({
  item,
  currencyCode,
}) => {
  const options = item.productVariantOptions?.map((option) => option.value).join(' / ');
  const thumbnail = item.featuredImage?.thumbnailUrl ?? item.productThumbnail ?? undefined;

  const commissionLabel = item.productCommission
    ? item.productCommission.basis === 'PROFIT'
      ? `Commission · ${item.productCommission.percentage}% of profit${
          // unitCost is only present when the merchant permits it.
          item.productCommission.unitCost != null
            ? ` (unit price less ${formatCents({ amount: item.productCommission.unitCost, display: 'none' })} cost)`
            : ''
        }`
      : `Commission · ${item.productCommission.percentage}% of product total`
    : undefined;

  return (
    <div className="border-b border-border/60 py-3.5 first:pt-0 last:border-b-0 last:pb-0">
      {/* Centred against the thumbnail, not top-aligned: the title block is two
          lines tall and the price and total were floating up beside the first
          of them instead of reading across the row. */}
      <div className="flex items-center gap-3">
        {/* The product's own photo, which the query previously never asked
            for — every line showed the same grey placeholder icon. */}
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={item.featuredImage?.altText ?? ''}
            className="h-11 w-11 shrink-0 rounded border border-border object-cover"
          />
        ) : (
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded border border-border bg-muted/40">
            <PhotoIcon className="h-5 w-5 text-muted-foreground/50" aria-hidden="true" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <Link
            to={`/products/${item.productId}`}
            className="text-sm font-medium text-primary hover:underline"
          >
            {`${item.productIdAlias} · ${item.title}`}
          </Link>
          <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            {options && <Badge variant="neutral">{options}</Badge>}
            {options && item.sku && <span aria-hidden="true">·</span>}
            {item.sku && <span>{`SKU ${item.sku}`}</span>}
          </p>
        </div>

        <span className="shrink-0 text-xs text-muted-foreground">
          {`${formatCents({ amount: item.unitPrice, currencyCode })} × ${item.quantity}`}
        </span>
        <span className="w-20 shrink-0 text-right text-sm font-semibold tabular-nums text-foreground">
          {formatCents({ amount: item.total, currencyCode })}
        </span>
      </div>

      {(commissionLabel || !!item.productFee) && (
        <div className="ml-14 mt-2 rounded-md bg-muted/40 px-3 py-1.5">
          {commissionLabel && item.productCommission && (
            <FeeLine
              label={commissionLabel}
              amount={item.productCommission.amount}
              currencyCode={currencyCode}
            />
          )}
          {!!item.productFee && (
            <FeeLine label="Product fee" amount={item.productFee} currencyCode={currencyCode} />
          )}
        </div>
      )}
    </div>
  );
};

/**
 * What was bought, with each item's own deductions attached to it.
 *
 * The fees for an item used to sit in a bordered sub-panel of its own with a
 * repeated "Total Fees" row, which read as a second table rather than as a
 * footnote to the line above it.
 */
export const OrderItems: React.FC<{
  items: SellerOrderItem[];
  currencyCode: string;
  /** Close the list with what the products come to. */
  showTotal?: boolean;
}> = ({ items, currencyCode, showTotal = false }) => {
  const unitCount = items.reduce((total, item) => total + item.quantity, 0);
  const productsTotal = items.reduce((total, item) => total + item.total, 0);
  // What the customer paid to have it sent. It is on the order and appeared on
  // no screen: a seller deciding whether to accept was shown the products
  // total and left to guess whether shipping was covered.
  const shippingPaid = items.reduce((total, item) => total + (item.shippingPrice ?? 0), 0);

  return (
    <SectionCard
      title="Items"
      testId="order-items-section"
      trailing={`${items.length} ${items.length === 1 ? 'product' : 'products'} · ${unitCount} ${unitCount === 1 ? 'item' : 'items'}`}
    >
      {items.map((item, index) => (
        <ItemRow item={item} currencyCode={currencyCode} key={`${item.productId}-${index}`} />
      ))}
      {/* Drawn on the order a seller is being asked to accept, where the sum
          of the lines is the thing being decided. On an order already in
          flight the payout card below carries it. */}
      {/* This row draws no line of its own: the total below draws a stronger
          one, and the two sat four pixels apart and read as a doubled
          hairline. */}
      {showTotal && shippingPaid > 0 && (
        <div className="flex items-baseline justify-between gap-4 py-2.5">
          <span className="text-sm text-muted-foreground">Shipping (customer paid)</span>
          <span className="text-sm font-medium tabular-nums text-foreground">
            {formatCents({ amount: shippingPaid, currencyCode })}
          </span>
        </div>
      )}
      {showTotal && (
        <div className="flex items-baseline justify-between gap-4 border-t border-border pt-3">
          <span className="text-sm font-semibold text-foreground">Products total</span>
          <span className="text-sm font-semibold tabular-nums text-foreground">
            {formatCents({ amount: productsTotal, currencyCode })}
          </span>
        </div>
      )}
    </SectionCard>
  );
};
